<template>
  <div class="searchbox">
    <fieldset class="form-group searchbox-group" :class="{ 'searchbox-disabled': disabled }">
      <legend
        v-if="!hidelabel || $slots.label"
        tabindex="-1"
        class="bv-no-focus-ring col-form-label pt-0 searchbox-label"
      >
        {{ labelFormatado }} <slot name="label" />
      </legend>

      <div ref="control" class="searchbox-control" :class="{ 'is-open': isDropdownOpen, 'has-selected': hasSelected, 'is-loading': isLoading }">
        <button
          v-if="hasSelected"
          type="button"
          class="searchbox-action searchbox-action-left"
          :disabled="disabled"
          :title="$t ? $t('Apagar') : 'Apagar'"
          @click="apagar"
        >
          <span aria-hidden="true">×</span>
          <span class="searchbox-action-text">{{ $t ? $t('Apagar') : 'Apagar' }}</span>
        </button>

        <div class="searchbox-input-wrap">
          <span class="searchbox-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path d="M10.8 18.1a7.3 7.3 0 1 1 5.15-2.13l4.04 4.04-1.41 1.41-4.04-4.04a7.25 7.25 0 0 1-3.74.72Zm0-2a5.3 5.3 0 1 0 0-10.6 5.3 5.3 0 0 0 0 10.6Z" />
            </svg>
          </span>

          <component
            :is="inputComponent"
            ref="searchInput"
            class="form-control searchbox-input-field"
            :class="inputClasses"
            :type="multiline ? undefined : 'text'"
            :rows="multiline ? 3 : undefined"
            :value="value"
            :placeholder="placeholder"
            :disabled="hasSelected || disabled"
            :aria-expanded="isDropdownOpen ? 'true' : 'false'"
            :aria-controls="dropdownId"
            autocomplete="off"
            @input="alterouCampo"
            @keyup.enter="pressEnter"
            @dblclick="pressEnter"
            @keydown="verificaNavegacao"
            @focus="entrouCampo"
            @blur="saiuCampo"
          />

          <span v-if="isLoading" role="status" class="searchbox-loading">
            <span class="searchbox-spinner" aria-hidden="true" />
            <span class="sr-only">Carregando</span>
          </span>
        </div>

        <button
          v-if="showIncluir"
          type="button"
          class="searchbox-action searchbox-action-right"
          @click="inclusao"
        >
          + Incluir
        </button>

        <button
          v-if="showAlterar"
          type="button"
          class="searchbox-action searchbox-action-right"
          @click="alteracao"
        >
          Alterar
        </button>
      </div>

      <small v-if="showHint" tabindex="-1" class="form-text searchbox-hint">
        {{ $t ? $t('Digite para pesquisar ou pressione Enter para listar todos os registros.') : 'Digite para pesquisar ou pressione Enter para listar todos os registros.' }}
      </small>
    </fieldset>

    <Teleport to="body">
      <transition name="searchbox-popover">
        <div
          v-if="isDropdownOpen"
          :id="dropdownId"
          ref="dropdown"
          class="searchbox-dropdown searchbox-dropdown-teleported"
          :class="`is-${dropdownPlacement}`"
          :style="dropdownStyle"
          role="listbox"
          @mousedown.prevent
        >
          <div class="searchbox-dropdown-header">
            <span class="searchbox-dropdown-title">Resultados encontrados</span>
            <span class="searchbox-dropdown-meta">{{ intervaloLabel }}</span>
          </div>

          <div class="searchbox-results">
            <button
              v-for="(item, index) in items"
              :key="item.ID || index"
              type="button"
              role="option"
              :aria-selected="indexNavigation === index ? 'true' : 'false'"
              class="searchbox-result"
              :class="{ selecionada: indexNavigation === index }"
              @mousedown.prevent="selecionarItem(index)"
            >
              <span class="searchbox-result-main" v-html="formatarDescricao(item.DESCRICAO || item.ID || 'Sem descrição')" />
              <span v-if="item.ID" class="searchbox-result-id">#{{ item.ID }}</span>
            </button>
          </div>

          <div class="searchbox-pagination" v-if="quantidadeTotal > quantidadePorPagina">
            <button type="button" class="searchbox-page-button" :disabled="pagina <= 1" @mousedown.prevent="voltarPagina">
              ← Anterior
            </button>
            <span class="searchbox-page-status">Página {{ pagina }}</span>
            <button type="button" class="searchbox-page-button" :disabled="!hasNextPage" @mousedown.prevent="avancarPagina">
              Próxima →
            </button>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script>
let searchBoxUid = 0;

export default {
  name: 'SearchBoxCore',
  props: {
    label: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    items: { type: Array, default: () => [] },
    quantidadeTotal: { type: Number, default: 0 },
    pagina: { type: Number, default: 1 },
    quantidadePorPagina: { type: Number, default: 10 },
    isLoading: { type: Boolean, default: false },
    value: { type: [String, Number], default: '' },
    selected: { type: Object, default: () => ({}) },
    incluir: { type: Boolean, default: false },
    alterar: { type: Boolean, default: false },
    tipo: { type: String, default: '' },
    hidelabel: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    multiline: { type: Boolean, default: false }
  },
  data() {
    searchBoxUid += 1;
    return {
      indexNavigation: null,
      focused: false,
      focusedOut: true,
      timerFocusOut: null,
      dropdownId: `searchbox-dropdown-${searchBoxUid}`,
      dropdownStyle: {},
      dropdownPlacement: 'bottom'
    };
  },
  computed: {
    inputComponent() {
      return this.multiline ? 'textarea' : 'input';
    },
    hasSelected() {
      return Boolean(this.selected && Object.keys(this.selected).length);
    },
    hasValue() {
      return Boolean(String(this.value || '').trim());
    },
    isDropdownOpen() {
      return this.items.length > 0 && !this.focusedOut;
    },
    hasNextPage() {
      return this.quantidadeTotal > this.pagina * this.quantidadePorPagina;
    },
    showIncluir() {
      return !this.hasSelected && this.incluir && this.hasValue && !this.isLoading && !this.disabled;
    },
    showAlterar() {
      return this.hasSelected && this.alterar && !this.isLoading && !this.disabled;
    },
    showHint() {
      return !this.items.length && !this.hidelabel && !this.hasSelected;
    },
    inputClasses() {
      return {
        'searchbox-input': this.isDropdownOpen,
        'is-valid': this.hasSelected
      };
    },
    intervaloLabel() {
      if (this.quantidadeTotal <= 0) return `${this.items.length} resultado${this.items.length === 1 ? '' : 's'}`;

      const de = (this.pagina - 1) * this.quantidadePorPagina + 1;
      const ate = Math.min(this.pagina * this.quantidadePorPagina, this.quantidadeTotal);
      return `${de}-${ate} de ${this.quantidadeTotal}`;
    },
    labelFormatado() {
      return this.label || '';
    }
  },
  watch: {
    isDropdownOpen(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.updateDropdownPosition();
          this.addFloatingListeners();
        });
      } else {
        this.removeFloatingListeners();
      }
    },
    items() {
      if (this.isDropdownOpen) this.$nextTick(this.updateDropdownPosition);
    },
    pagina() {
      if (this.isDropdownOpen) this.$nextTick(this.updateDropdownPosition);
    }
  },

  beforeDestroy() {
    this.cancelarTimerFocusOut();
    this.removeFloatingListeners();
  },
  beforeUnmount() {
    this.cancelarTimerFocusOut();
    this.removeFloatingListeners();
  },
  methods: {
    addFloatingListeners() {
      if (typeof window === 'undefined') return;
      window.addEventListener('scroll', this.updateDropdownPosition, true);
      window.addEventListener('resize', this.updateDropdownPosition);
    },
    removeFloatingListeners() {
      if (typeof window === 'undefined') return;
      window.removeEventListener('scroll', this.updateDropdownPosition, true);
      window.removeEventListener('resize', this.updateDropdownPosition);
    },
    updateDropdownPosition() {
      if (typeof window === 'undefined') return;

      const control = this.$refs.control;
      if (!control || !this.isDropdownOpen) return;

      const rect = control.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const gap = 4;
      const viewportPadding = 8;
      const preferredMaxHeight = 360;
      const availableBelow = viewportHeight - rect.bottom - gap - viewportPadding;
      const availableAbove = rect.top - gap - viewportPadding;
      const shouldOpenAbove = availableBelow < 220 && availableAbove > availableBelow;
      const maxHeight = Math.max(160, Math.min(preferredMaxHeight, shouldOpenAbove ? availableAbove : availableBelow));
      const left = Math.min(Math.max(rect.left, viewportPadding), Math.max(viewportPadding, viewportWidth - rect.width - viewportPadding));

      this.dropdownPlacement = shouldOpenAbove ? 'top' : 'bottom';
      this.dropdownStyle = {
        position: 'fixed',
        top: shouldOpenAbove ? `${Math.max(viewportPadding, rect.top - gap - maxHeight)}px` : `${rect.bottom + gap}px`,
        left: `${left}px`,
        width: `${rect.width}px`,
        maxHeight: `${maxHeight}px`,
        zIndex: 999999
      };
    },
    cancelarTimerFocusOut() {
      if (this.timerFocusOut) {
        clearTimeout(this.timerFocusOut);
        this.timerFocusOut = null;
      }
    },
    alterouCampo(event) {
      const valor = event.target.value;
      this.$emit('alteracao', this.multiline && valor.trim() === '' ? '' : valor);
      this.indexNavigation = null;
    },
    pressEnter() {
      if (this.indexNavigation === null) {
        this.$emit('pressEnter');
        return;
      }

      const index = this.indexNavigation;
      this.indexNavigation = null;
      this.$emit('selecionarItem', index);
    },
    entrouCampo() {
      this.focused = true;
      this.focusedOut = false;
      this.cancelarTimerFocusOut();
      this.$nextTick(this.updateDropdownPosition);
    },
    saiuCampo() {
      this.focused = false;
      this.cancelarTimerFocusOut();
      this.timerFocusOut = setTimeout(() => {
        this.focusedOut = true;
        this.indexNavigation = null;
        this.removeFloatingListeners();
      }, 180);
    },
    colocarFoco() {
      this.$nextTick(() => {
        if (this.$refs.searchInput) this.$refs.searchInput.focus();
      });
    },
    selecionarItem(index) {
      this.indexNavigation = null;
      this.$emit('selecionarItem', index);
    },
    voltarPagina() {
      this.indexNavigation = null;
      this.$emit('voltarPagina');
    },
    avancarPagina() {
      this.indexNavigation = null;
      this.$emit('avancarPagina');
    },
    apagar() {
      this.indexNavigation = null;
      this.$emit('apagar');
    },
    verificaNavegacao(event) {
      const key = event.key;

      if (key === 'PageUp') {
        event.preventDefault();
        if (this.pagina > 1) this.voltarPagina();
        return;
      }

      if (key === 'PageDown') {
        event.preventDefault();
        if (this.hasNextPage) this.avancarPagina();
        return;
      }

      if (key === 'ArrowUp') {
        event.preventDefault();
        if (!this.items.length) return;
        this.indexNavigation = this.indexNavigation === null
          ? this.items.length - 1
          : Math.max(this.indexNavigation - 1, 0);
        return;
      }

      if (key === 'ArrowDown') {
        event.preventDefault();
        if (!this.items.length) return;
        this.indexNavigation = this.indexNavigation === null
          ? 0
          : Math.min(this.indexNavigation + 1, this.items.length - 1);
        return;
      }

      if (key === 'Escape') {
        this.focusedOut = true;
        this.indexNavigation = null;
      }
    },
    inclusao() {
      if (this.tipo === 'emitente') {
        this.$router.push({ path: `/cadastros/emitente/0/${this.value}` });
        return;
      }
      if (this.tipo === 'paciente') {
        this.$router.push({ path: '/paciente' });
        return;
      }
      this.$emit('botaoInclusao');
    },
    alteracao() {
      if (this.tipo === 'emitente') {
        this.$router.push({ path: `/cadastros/emitente/${this.selected.ID}` });
        return;
      }
      if (this.tipo === 'paciente') {
        this.$router.push({ path: `/paciente/${this.selected.ID}` });
        return;
      }
      this.$emit('botaoAlteracao');
    },
    setFocus() {
      if (this.$refs.searchInput) this.$refs.searchInput.focus();
    },
    escapeHtml(texto = '') {
      return String(texto)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },
    formatarDescricao(texto) {
      const escaped = this.escapeHtml(texto);
      const withAllowedTags = escaped
        .replace(/&lt;b&gt;/gi, '<b>')
        .replace(/&lt;\/b&gt;/gi, '</b>')
        .replace(/&lt;br\s*\/?&gt;/gi, '<br/>');

      return this.multiline ? withAllowedTags.replace(/\n/g, '<br/>') : withAllowedTags;
    }
  }
};
</script>

<style scoped>
.searchbox,
.searchbox-dropdown {
  /* Usa as variáveis do tema do sistema */
  --sb-border: var(--kt-input-border-color, #d0d5dd);
  --sb-bg: var(--kt-input-bg, #ffffff);
  --sb-text: var(--kt-input-color, #101828);

  /* Tokens locais derivados; se quiser, também podem ser sobrescritos no layout. */
  --sb-primary: var(--kt-primary, #30e3da);
  --sb-primary-strong: var(--kt-primary-active, #12b76a);
  --sb-muted: color-mix(in srgb, var(--sb-text) 62%, transparent);
  --sb-muted-soft: color-mix(in srgb, var(--sb-text) 42%, transparent);
  --sb-surface-soft: color-mix(in srgb, var(--sb-bg) 92%, var(--sb-text) 8%);
  --sb-surface-hover: color-mix(in srgb, var(--sb-bg) 86%, var(--sb-primary) 14%);
  --sb-divider: color-mix(in srgb, var(--sb-border) 78%, transparent);
  --sb-shadow: rgba(0, 0, 0, 0.16);
  --sb-focus-shadow: color-mix(in srgb, var(--sb-primary) 22%, transparent);
}

.searchbox { position: relative; width: 100%; color: var(--sb-text); }
.searchbox-group { margin-bottom: 0; }
.searchbox-group > .searchbox-label + .searchbox-control { margin-top: 0; }
.searchbox-label { display: inline-flex; align-items: center; gap: 4px; margin-bottom: 2px; padding-bottom: 0; color: var(--sb-text); font-size: 0.84rem; font-weight: 700; letter-spacing: 0.01em; }
.searchbox-disabled { opacity: 0.72; }
.searchbox-control { display: flex; align-items: stretch; height: 33px; min-height: 33px; border: 1px solid var(--sb-border); border-radius: 12px; background: var(--sb-bg); box-shadow: 0 1px 2px var(--sb-shadow); transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease; }
.searchbox-control:hover { border-color: var(--sb-muted-soft); }
.searchbox-control:focus-within, .searchbox-control.is-open { border-color: var(--sb-primary); box-shadow: 0 0 0 2px var(--sb-focus-shadow), 0 12px 24px var(--sb-shadow); }
.searchbox-control.has-selected { border-color: var(--sb-primary-strong); background: var(--sb-bg); }
.searchbox-control.is-open { border-radius: 12px; }
.searchbox-input-wrap { position: relative; display: flex; align-items: center; flex: 1 1 auto; min-width: 0; }
.searchbox-icon { position: absolute; left: 13px; display: inline-flex; width: 16px; height: 16px; color: var(--sb-muted); pointer-events: none; }
.searchbox-icon svg { width: 16px; height: 16px; fill: currentColor; }
.searchbox-input-field.form-control { height: 31px; min-height: 31px; width: 100%; padding: 4px 42px 4px 40px; border: 0 !important; border-radius: 12px; background: transparent !important; color: var(--sb-text); font-size: 0.9rem; line-height: 1.25; box-shadow: none !important; }
textarea.searchbox-input-field.form-control { min-height: 82px; resize: vertical; }
.searchbox-input-field::placeholder { color: var(--sb-muted-soft); }
.searchbox-input-field:disabled { cursor: not-allowed; color: var(--sb-text); opacity: 0.72; }
.searchbox-loading { position: absolute; right: 13px; display: inline-flex; align-items: center; justify-content: center; }
.searchbox-spinner { width: 15px; height: 15px; border: 2px solid color-mix(in srgb, var(--sb-primary) 28%, transparent); border-top-color: var(--sb-primary-strong); border-radius: 999px; animation: searchbox-spin 0.72s linear infinite; }
.searchbox-action { 
  display: inline-flex; 
  align-items: center; 
  justify-content: center; 
  gap: 6px; 
  min-width: 76px; 
  padding: 0 12px; 
  border: 0; 
  color: #ffffff; 
  font-size: 0.82rem; 
  font-weight: 700; 
  white-space: nowrap; 
  background: linear-gradient(135deg, var(--sb-primary) 0%, var(--sb-primary-strong) 100%); 
  transition: filter 0.16s ease, transform 0.16s ease, opacity 0.16s ease; 
}
.searchbox-action:hover:not(:disabled) { filter: brightness(0.98); }
.searchbox-action:active:not(:disabled) { transform: translateY(1px); }
.searchbox-action:disabled { cursor: not-allowed; opacity: 0.6; }
.searchbox-action-left { border-top-left-radius: 11px; border-bottom-left-radius: 11px; }
.searchbox-action-left span[aria-hidden="true"] { font-size: 1.15rem; line-height: 1; }
.searchbox-action-right { border-top-right-radius: 11px; border-bottom-right-radius: 11px; }
.searchbox-control.is-open .searchbox-action-left { border-bottom-left-radius: 11px; } .searchbox-control.is-open .searchbox-action-right { border-bottom-right-radius: 11px; }
.searchbox-hint { margin-top: 6px; color: var(--sb-muted); font-size: 0.77rem; }
.searchbox-dropdown { position: fixed; z-index: 999999; display: flex; flex-direction: column; overflow: hidden; border: 1px solid var(--sb-primary); border-radius: 14px; background: var(--sb-bg); color: var(--sb-text); box-shadow: 0 18px 42px var(--sb-shadow); }
.searchbox-dropdown-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 12px; border-bottom: 1px solid var(--sb-divider); background: var(--sb-surface-soft); }
.searchbox-dropdown-title { color: var(--sb-text); font-size: 0.78rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em; }
.searchbox-dropdown-meta { display: inline-flex; align-items: center; padding: 3px 8px; border-radius: 999px; color: var(--sb-primary-strong); background: color-mix(in srgb, var(--sb-bg) 82%, var(--sb-primary-strong) 18%); font-size: 0.75rem; font-weight: 700; }
.searchbox-results { flex: 1 1 auto; min-height: 0; overflow-y: auto; overscroll-behavior: contain; }
.searchbox-result { display: flex; align-items: center; justify-content: space-between; gap: 12px; width: 100%; padding: 11px 12px; border: 0; border-bottom: 1px solid var(--sb-divider); color: var(--sb-text); background: var(--sb-bg); text-align: left; cursor: pointer; transition: background 0.14s ease, color 0.14s ease, transform 0.14s ease; }
.searchbox-result:hover, .searchbox-result.selecionada { background: var(--sb-surface-hover); }
.searchbox-result.selecionada { box-shadow: inset 3px 0 0 var(--sb-primary); }
.searchbox-result-main { min-width: 0; overflow: hidden; color: inherit; font-size: 0.92rem; line-height: 1.35; text-overflow: ellipsis; }
.searchbox-result-main >>> b, .searchbox-result-main ::deep(b) { color: var(--sb-primary-strong); font-weight: 800; background: color-mix(in srgb, var(--sb-bg) 76%, var(--sb-primary-strong) 24%); border-radius: 4px; padding: 0 2px; }
.searchbox-result-id { flex: 0 0 auto; color: var(--sb-muted); font-size: 0.78rem; font-weight: 800; }
.searchbox-pagination { display: grid; grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr); align-items: center; gap: 8px; padding: 10px 12px; border-top: 1px solid var(--sb-divider); background: var(--sb-surface-soft); }
.searchbox-page-button { min-height: 34px; padding: 0 12px; border: 1px solid var(--sb-border); border-radius: 9px; color: var(--sb-text); background: var(--sb-bg); font-size: 0.82rem; font-weight: 800; transition: background 0.14s ease, border-color 0.14s ease, transform 0.14s ease; }
.searchbox-page-button:hover:not(:disabled) { border-color: var(--sb-primary); background: var(--sb-surface-hover); }
.searchbox-page-button:active:not(:disabled) { transform: translateY(1px); }
.searchbox-page-button:disabled { cursor: not-allowed; color: var(--sb-muted-soft); background: var(--sb-surface-soft); }
.searchbox-page-status { color: var(--sb-muted); font-size: 0.78rem; font-weight: 800; white-space: nowrap; }
.searchbox-popover-enter-active, .searchbox-popover-leave-active { transition: opacity 0.16s ease, transform 0.16s ease; }
.searchbox-popover-enter, .searchbox-popover-leave-to { opacity: 0; transform: translateY(-4px); }
@keyframes searchbox-spin { to { transform: rotate(360deg); } }
@media (max-width: 767px) { 
  .searchbox-control { min-height: 40px; border-radius: 10px; } 
  .searchbox-input-field.form-control { min-height: 38px; padding-left: 38px; font-size: 0.9rem; } 
  .searchbox-action { min-width: auto; padding: 0 10px; font-size: 0.78rem; } 
  .searchbox-action-text { display: none; } 
  .searchbox-dropdown { border-bottom-right-radius: 12px; border-bottom-left-radius: 12px; } 
  .searchbox-dropdown-header { align-items: flex-start; flex-direction: column; gap: 6px; } 
  .searchbox-result { align-items: flex-start; flex-direction: column; gap: 4px; } 
  .searchbox-pagination { grid-template-columns: 1fr; } 
  .searchbox-page-status { order: -1; text-align: center; } 
}
</style>

